import { Link } from 'react-router';
import { useState, useEffect, useRef } from 'react';

export default function Rules() {
  const [activeSection, setActiveSection] = useState(0);
  const [circleSize, setCircleSize] = useState(100);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Animate circle shrinking demo
  useEffect(() => {
    const interval = setInterval(() => {
      setCircleSize(prev => {
        if (prev <= 20) return 100;
        return prev - 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Section observer for navigation highlight
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-section'));
            setActiveSection(index);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -50% 0px' }
    );

    document.querySelectorAll('[data-section]').forEach((section) => {
      observerRef.current?.observe(section);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const sections = [
    { id: 'objective', label: 'OBJ', title: 'Objective' },
    { id: 'mechanics', label: 'MCH', title: 'Mechanics' },
    { id: 'credits', label: 'CRD', title: 'Credits' },
    { id: 'eligibility', label: 'ELG', title: 'Eligibility' },
    { id: 'season', label: 'SSN', title: 'Season' },
    { id: 'fairplay', label: 'SEC', title: 'Fair Play' },
  ];

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-hidden">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Grid background */}
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(to right, white 1px, transparent 1px),
              linear-gradient(to bottom, white 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        {/* Radial fade */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-[#030303]/50 to-[#030303]" />
      </div>

      {/* Fixed side navigation */}
      <nav className="fixed left-6 top-1/2 -translate-y-1/2 z-50 hidden lg:block">
        <div className="flex flex-col gap-1">
          {sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`group flex items-center gap-3 py-2 transition-all duration-300 ${
                activeSection === index ? 'opacity-100' : 'opacity-30 hover:opacity-60'
              }`}
            >
              <span
                className={`font-mono text-[10px] tracking-wider transition-colors ${
                  activeSection === index ? 'text-white' : 'text-white/50'
                }`}
              >
                {section.label}
              </span>
              <span
                className={`h-px transition-all duration-300 ${
                  activeSection === index
                    ? 'w-8 bg-white'
                    : 'w-4 bg-white/30 group-hover:w-6 group-hover:bg-white/50'
                }`}
              />
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        {/* Header */}
        <header className="mb-24 animate-slide-up-fade">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <span className="font-mono text-[10px] tracking-[0.4em] text-white/30">
              TECHNICAL MANUAL
            </span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>

          <h1 className="font-display text-[clamp(4rem,12vw,10rem)] leading-[0.85] tracking-[0.02em] text-center mb-6">
            <span className="block text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/30">
              GAME
            </span>
            <span className="block text-white/20">RULES</span>
          </h1>

          <p className="font-mono text-sm text-white/40 text-center max-w-xl mx-auto tracking-wide">
            Complete specification for <span className="text-white/70">HOLD STEADY</span> gameplay mechanics,
            eligibility requirements, and competition structure.
          </p>

          {/* Version tag */}
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-3 px-4 py-2 border border-white/10 bg-white/[0.02]">
              <span className="font-mono text-[10px] text-white/30">VERSION</span>
              <span className="font-mono text-xs text-white/60">1.0.0</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
            </div>
          </div>
        </header>

        {/* Section 01: Objective */}
        <section
          id="objective"
          data-section="0"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.1s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">01</span>
            <h2 className="font-display text-4xl tracking-wide">THE OBJECTIVE</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Interactive demo */}
            <div className="relative w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] lg:w-[384px] lg:h-[384px] mx-auto lg:mx-0">
              {/* Measurement lines - hidden on mobile */}
              <div className="hidden sm:flex absolute -left-12 top-0 bottom-0 flex-col justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] text-white/20">200px</span>
                  <div className="w-3 h-px bg-white/20" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] text-white/20">20px</span>
                  <div className="w-3 h-px bg-white/20" />
                </div>
              </div>

              {/* Background grid */}
              <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, white 1px, transparent 1px),
                    linear-gradient(to bottom, white 1px, transparent 1px)
                  `,
                  backgroundSize: '20px 20px',
                }}
              />

              {/* Outer reference circle */}
              <div className="absolute inset-4 rounded-full border border-dashed border-white/10" />

              {/* Shrinking circle */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/80 transition-all duration-100"
                style={{
                  width: `${circleSize}%`,
                  height: `${circleSize}%`,
                  boxShadow: circleSize < 40
                    ? '0 0 30px rgba(239, 68, 68, 0.3)'
                    : '0 0 20px rgba(255, 255, 255, 0.1)',
                }}
              />

              {/* Center cursor */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-3 h-3 rounded-full bg-white shadow-[0_0_15px_rgba(255,255,255,0.8)]" />
              </div>

              {/* Timer display - centered on mobile, bottom-right on larger */}
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 font-mono text-base sm:text-lg text-white/50">
                {((100 - circleSize) * 0.45 + 0.01).toFixed(2)}s
              </div>

              {/* Status indicator - top center on mobile */}
              <div className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 sm:left-4 sm:translate-x-0 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
                <span className="font-mono text-[10px] text-white/40 tracking-wider">ACTIVE</span>
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="space-y-6">
                <div className="p-6 border border-white/10 bg-white/[0.02]">
                  <div className="flex items-start gap-4">
                    <div className="font-mono text-2xl text-white/20">45</div>
                    <div>
                      <div className="font-mono text-[10px] text-white/40 tracking-wider mb-1">
                        SECONDS
                      </div>
                      <p className="text-white/60 leading-relaxed">
                        Keep your cursor inside the shrinking circle for the full duration.
                        The circle begins at <span className="font-mono text-white">200px</span> and
                        shrinks to just <span className="font-mono text-white">20px</span>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-white/10 bg-white/[0.02]">
                  <div className="flex items-start gap-4">
                    <div className="font-mono text-2xl text-white/20">100</div>
                    <div>
                      <div className="font-mono text-[10px] text-white/40 tracking-wider mb-1">
                        MILLISECONDS
                      </div>
                      <p className="text-white/60 leading-relaxed">
                        Maximum time allowed outside the boundary. Exceed this threshold
                        and you're <span className="text-[#ef4444]">eliminated instantly</span>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-[#22c55e]/20 bg-[#22c55e]/[0.03]">
                  <div className="flex items-start gap-4">
                    <svg className="w-6 h-6 text-[#22c55e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[#22c55e]/80 text-sm leading-relaxed">
                      Your score is measured in <span className="text-[#22c55e] font-semibold">milliseconds survived</span>.
                      Complete all 45 seconds (45,000ms) to post a perfect run.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 02: Mechanics */}
        <section
          id="mechanics"
          data-section="1"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.15s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">02</span>
            <h2 className="font-display text-4xl tracking-wide">MECHANICS</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                    <path strokeLinecap="round" strokeWidth={1.5} d="M12 6v6l4 2" />
                  </svg>
                ),
                label: 'SHRINK RATE',
                title: 'Linear Reduction',
                desc: 'Circle radius decreases linearly from start to end. No acceleration, no randomness in shrink speed.',
                specs: ['200px → 20px', '45 seconds', 'Linear curve'],
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                label: 'JITTER',
                title: 'Random Movement',
                desc: 'The circle jitters unpredictably. Amplitude increases as the game progresses, testing your reflexes.',
                specs: ['2px → 8px amplitude', 'Random direction', 'Scaling intensity'],
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                ),
                label: 'DRIFT',
                title: 'Position Movement',
                desc: 'The circle center drifts across the play area. Smooth but unpredictable trajectories keep you moving.',
                specs: ['Variable speed', 'Bounded area', 'Smooth curves'],
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                label: 'DETECTION',
                title: 'Exit Tracking',
                desc: 'Server validates cursor position in real-time. Exit detection has 100ms grace period before elimination.',
                specs: ['Server-authoritative', '100ms threshold', 'Instant feedback'],
              },
            ].map((item) => (
              <div
                key={item.label}
                className="group p-6 border border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.03] hover:border-white/10 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white/5 text-white/50 group-hover:text-white/80 transition-colors">
                    {item.icon}
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-white/30">
                    {item.label}
                  </span>
                </div>
                <h3 className="font-display text-xl tracking-wide mb-2">{item.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {item.specs.map((spec) => (
                    <span
                      key={spec}
                      className="font-mono text-[10px] px-2 py-1 bg-white/5 text-white/50"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 03: Credits */}
        <section
          id="credits"
          data-section="2"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.2s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">03</span>
            <h2 className="font-display text-4xl tracking-wide">RUN CREDITS</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main credit info */}
            <div className="lg:col-span-2 p-8 border border-white/10 bg-white/[0.02]">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <div className="font-mono text-[10px] text-white/30 tracking-wider mb-2">
                    CREDIT SYSTEM
                  </div>
                  <h3 className="font-display text-3xl tracking-wide">Pay to Play</h3>
                </div>
                <div className="p-3 bg-[#fbbf24]/10 border border-[#fbbf24]/20">
                  <svg className="w-8 h-8 text-[#fbbf24]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <span className="text-white/50">Cost per attempt</span>
                  <span className="font-mono text-white">1 CREDIT</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <span className="text-white/50">Credit pack price</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[#22c55e]">0.1 SOL</span>
                    <span className="text-white/30">=</span>
                    <span className="font-mono text-white">5 credits</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <span className="text-white/50">Daily pack limit</span>
                  <span className="font-mono text-white">2 PACKS</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-white/50">Credit expiration</span>
                  <span className="font-mono text-[#ef4444]">00:00 UTC DAILY</span>
                </div>
              </div>
            </div>

            {/* Prize pool contribution */}
            <div className="p-8 border border-[#fbbf24]/20 bg-[#fbbf24]/[0.03] flex flex-col">
              <div className="font-mono text-[10px] text-[#fbbf24]/60 tracking-wider mb-2">
                PRIZE CONTRIBUTION
              </div>
              <div className="font-display text-6xl text-[#fbbf24] mb-3">50%</div>
              <p className="text-[#fbbf24]/60 text-sm leading-relaxed flex-1">
                Half of all credit purchases flow directly into the prize pool.
                More players = bigger prizes.
              </p>
              <div className="mt-6 pt-4 border-t border-[#fbbf24]/20">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#22c55e] animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="font-mono text-[10px] text-[#22c55e]/80">POOL GROWING</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 04: Eligibility */}
        <section
          id="eligibility"
          data-section="3"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.25s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">04</span>
            <h2 className="font-display text-4xl tracking-wide">ELIGIBILITY</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                requirement: '25,000',
                unit: '$BG',
                title: 'Token Holding',
                desc: 'Minimum token balance required in your connected wallet to participate.',
                status: 'REQUIRED',
              },
              {
                requirement: '1+',
                unit: 'CREDIT',
                title: 'Run Credit',
                desc: 'At least one valid credit needed to start a game attempt.',
                status: 'REQUIRED',
              },
              {
                requirement: 'SOL',
                unit: 'WALLET',
                title: 'Wallet Connection',
                desc: 'Phantom or Solflare wallet on Solana mainnet.',
                status: 'REQUIRED',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 border border-white/10 bg-white/[0.02] hover:border-white/20 transition-colors"
              >
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-display text-4xl text-white">{item.requirement}</span>
                  <span className="font-mono text-sm text-white/40 mb-1">{item.unit}</span>
                </div>
                <div className="font-mono text-[10px] tracking-wider text-white/30 mb-2">
                  {item.status}
                </div>
                <h3 className="font-display text-lg tracking-wide mb-2">{item.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 05: Season Structure */}
        <section
          id="season"
          data-section="4"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.3s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">05</span>
            <h2 className="font-display text-4xl tracking-wide">SEASON STRUCTURE</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="space-y-4">
            {[
              {
                phase: '01',
                name: 'QUALIFIERS',
                players: 'ALL',
                advance: '256',
                difficulty: '200px',
                jitter: '2-8px',
                duration: '3 days',
                color: '#22c55e',
              },
              {
                phase: '02',
                name: 'SHORTLIST',
                players: '256',
                advance: '64',
                difficulty: '150px',
                jitter: '4-10px',
                duration: '2 days',
                color: '#3b82f6',
              },
              {
                phase: '03',
                name: 'SEMIFINALS',
                players: '64',
                advance: '16',
                difficulty: '120px',
                jitter: '6-14px',
                duration: '1 day',
                color: '#a855f7',
              },
              {
                phase: '04',
                name: 'FINALS',
                players: '16',
                advance: '4',
                difficulty: '100px',
                jitter: '8-18px',
                duration: '1 day',
                color: '#fbbf24',
                isLive: true,
              },
            ].map((item, index) => (
              <div
                key={item.phase}
                className="group relative flex items-stretch border border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.02] hover:border-white/10 transition-all overflow-hidden"
              >
                {/* Phase color indicator */}
                <div
                  className="w-1 flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />

                <div className="flex-1 p-6 flex items-center gap-8">
                  {/* Phase number */}
                  <div className="hidden sm:block">
                    <div className="font-mono text-3xl text-white/10">{item.phase}</div>
                  </div>

                  {/* Phase name */}
                  <div className="min-w-[140px]">
                    <div
                      className="font-display text-xl tracking-wide"
                      style={{ color: item.color }}
                    >
                      {item.name}
                    </div>
                    {item.isLive && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] animate-pulse" />
                        <span className="font-mono text-[9px] text-[#fbbf24]/70 tracking-wider">
                          BROADCAST
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Stats grid */}
                  <div className="hidden md:flex items-center gap-8 flex-1">
                    <div>
                      <div className="font-mono text-[9px] text-white/30 tracking-wider mb-1">
                        DURATION
                      </div>
                      <div className="font-mono text-sm text-white/70">{item.duration}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] text-white/30 tracking-wider mb-1">
                        PLAYERS
                      </div>
                      <div className="font-mono text-sm text-white/70">{item.players}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] text-white/30 tracking-wider mb-1">
                        ADVANCE
                      </div>
                      <div className="font-mono text-sm text-white/70">Top {item.advance}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] text-white/30 tracking-wider mb-1">
                        START SIZE
                      </div>
                      <div className="font-mono text-sm text-white/70">{item.difficulty}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[9px] text-white/30 tracking-wider mb-1">
                        JITTER
                      </div>
                      <div className="font-mono text-sm text-white/70">{item.jitter}</div>
                    </div>
                  </div>

                  {/* Arrow to next */}
                  {index < 3 && (
                    <div className="hidden lg:block text-white/10">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Difficulty scaling note */}
          <div className="mt-8 p-6 border border-[#f59e0b]/20 bg-[#f59e0b]/[0.03]">
            <div className="flex items-start gap-4">
              <svg className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <div>
                <div className="font-mono text-[10px] text-[#f59e0b]/60 tracking-wider mb-1">
                  DIFFICULTY SCALING
                </div>
                <p className="text-[#f59e0b]/80 text-sm leading-relaxed">
                  Each phase increases the challenge. Starting circle shrinks, jitter amplitude grows,
                  and movement speed intensifies. Only the most precise players advance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 06: Fair Play */}
        <section
          id="fairplay"
          data-section="5"
          className="mb-32 animate-slide-up-fade"
          style={{ animationDelay: '0.35s' }}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="font-mono text-xs text-white/20">06</span>
            <h2 className="font-display text-4xl tracking-wide">FAIR PLAY</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Security measures */}
            <div className="p-8 border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-white/5">
                  <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] text-white/30">
                  INTEGRITY SYSTEM
                </span>
              </div>

              <h3 className="font-display text-2xl tracking-wide mb-4">Server-Side Validation</h3>
              <p className="text-white/40 leading-relaxed mb-6">
                All game state is calculated and validated on the server. Circle position is determined
                using a cryptographic seed — you can't predict or manipulate it.
              </p>

              <div className="space-y-3">
                {[
                  'Deterministic circle positioning',
                  'Real-time position validation',
                  'Cryptographic seed generation',
                  'Anti-tampering checksums',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-white/60 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prohibited actions */}
            <div className="p-8 border border-[#ef4444]/20 bg-[#ef4444]/[0.03]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[#ef4444]/10">
                  <svg className="w-5 h-5 text-[#ef4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] text-[#ef4444]/60">
                  PROHIBITED
                </span>
              </div>

              <h3 className="font-display text-2xl tracking-wide mb-4 text-[#ef4444]">
                Instant Disqualification
              </h3>
              <p className="text-[#ef4444]/60 leading-relaxed mb-6">
                The following actions result in immediate and permanent ban from competition:
              </p>

              <div className="space-y-3">
                {[
                  'Automation or macro software',
                  'Bot-assisted gameplay',
                  'Network manipulation',
                  'Client modification',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-[#ef4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-[#ef4444]/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-[#ef4444]/20">
                <p className="text-[#ef4444]/50 text-xs">
                  Our detection monitors teleportation, inhuman precision, timing anomalies, and other patterns.
                  Flagged accounts are reviewed or auto-invalidated.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center pt-8 border-t border-white/[0.06]">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-white/20" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-white/30">BEGIN</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-white/20" />
          </div>

          <Link
            to="/play"
            className="group inline-flex items-center gap-4 px-12 py-5 bg-white text-black font-bold tracking-[0.15em] uppercase transition-all duration-300 hover:shadow-[0_0_60px_rgba(255,255,255,0.3)] hover:gap-6"
          >
            <span>Enter The Arena</span>
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>

          <p className="mt-6 font-mono text-xs text-white/30">
            Good luck. You'll need it.
          </p>
        </div>
      </div>
    </div>
  );
}
